import { useEffect, useState } from 'react';
import {
  PAYMENT_TIER_FREE,
  type PaidTier,
  type PaymentTier,
  type QuotaPayload,
  type TierCatalogEntry,
} from '@mondy/shared';
import { ApiError } from '@mondy/shared/api-client';
import { useTierCatalog } from '@/hooks/useTierCatalog';
import { useStartCheckout } from '@/hooks/useStartCheckout';
import { Spinner } from '@/components/Spinner';

export type UpgradeReason =
  | {
      code: 'GROUP_QUOTA_EXCEEDED' | 'MEMBER_QUOTA_EXCEEDED';
      payload: QuotaPayload;
    }
  | {
      // Manually surfaced (no 402) when promoting the early-prediction perk.
      code: 'EARLY_PREDICTION';
      requiredTier: PaidTier;
      currentTier: PaymentTier;
    }
  | {
      // Manually surfaced when promoting the view-others-early perk
      // (separate gate, lower tier than EARLY_PREDICTION).
      code: 'VIEW_PREDICTIONS_EARLY';
      requiredTier: PaidTier;
      currentTier: PaymentTier;
    };

/**
 * Open the upgrade modal off any 402 thrown from the api-client. The shape
 * matches the server-side `paymentRequired()` envelope. Returns `null` if the
 * error isn't a quota error so callers can fall through to their generic
 * error handling.
 */
export function upgradeReasonFromApiError(err: unknown): UpgradeReason | null {
  if (!(err instanceof ApiError)) return null;
  if (err.code !== 'GROUP_QUOTA_EXCEEDED' && err.code !== 'MEMBER_QUOTA_EXCEEDED') {
    return null;
  }
  const payload = err.payload as QuotaPayload | undefined;
  if (!payload) return null;
  return { code: err.code, payload };
}

function reasonHeadline(reason: UpgradeReason): { title: string; body: string } {
  if (reason.code === 'GROUP_QUOTA_EXCEEDED') {
    return {
      title: 'Upgrade to create more groups',
      body: 'Your current plan does not let you create another group. Pick a tier to unlock it.',
    };
  }
  if (reason.code === 'MEMBER_QUOTA_EXCEEDED') {
    return {
      title: 'Upgrade to invite more members',
      body: "This group has reached its member limit on the owner's current plan. Pick a tier to lift the cap.",
    };
  }
  if (reason.code === 'VIEW_PREDICTIONS_EARLY') {
    return {
      title: 'See predictions before kickoff with Gold',
      body: "Gold unlocks your group's predictions instantly — no need to wait for kickoff.",
    };
  }
  return {
    title: 'Predict early with Platinum',
    body: 'Skip the 24-hour prediction window — Platinum lets you submit picks for any match with confirmed teams as soon as you’re ready.',
  };
}

export function UpgradeModal({
  open,
  onClose,
  reason,
}: {
  open: boolean;
  onClose: () => void;
  reason: UpgradeReason | null;
}) {
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open || !reason) return null;
  const { title, body } = reasonHeadline(reason);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="upgrade-modal-title"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 100,
        background: 'rgba(0, 0, 0, 0.6)',
        overflowY: 'auto',
      }}
    >
      <div
        style={{
          minHeight: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding:
            'max(16px, env(safe-area-inset-top)) 16px max(16px, env(safe-area-inset-bottom))',
        }}
      >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-[640px] p-5 sm:p-6"
        style={{
          position: 'relative',
          // Two-layer background trick: the inner `linear-gradient(--surface, --surface)`
          // paints the modal body, while the outer team-colored gradient is
          // clipped to the border-box so it only shows through the 2px top
          // border. Gives the modal a subtle team-colored cap.
          border: '1px solid var(--line)',
          borderTop: '2px solid transparent',
          backgroundImage:
            'linear-gradient(var(--surface), var(--surface)), linear-gradient(90deg, var(--team-primary), var(--team-accent))',
          backgroundOrigin: 'border-box',
          backgroundClip: 'padding-box, border-box',
          borderRadius: 'var(--radius-lg)',
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
        }}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="cursor-pointer text-[color:var(--color-fg)]"
          style={{
            position: 'sticky',
            top: 8,
            alignSelf: 'flex-end',
            marginBottom: -36,
            zIndex: 1,
            width: 36,
            height: 36,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 999,
            border: '1px solid var(--line)',
            background: 'color-mix(in oklab, var(--surface) 80%, transparent)',
            backdropFilter: 'blur(6px)',
            WebkitBackdropFilter: 'blur(6px)',
            fontSize: 16,
            lineHeight: 1,
          }}
        >
          ✕
        </button>

        <header>
          <h2
            id="upgrade-modal-title"
            style={{
              margin: 0,
              paddingRight: 40,
              fontSize: 22,
              fontWeight: 600,
              lineHeight: 1.2,
              fontFamily: 'var(--team-font)',
              letterSpacing: '0.01em',
            }}
          >
            {title}
          </h2>
          <p
            style={{
              margin: '8px 0 0',
              fontSize: 14,
              color: 'var(--fg-muted)',
              lineHeight: 1.45,
            }}
          >
            {body}
          </p>
        </header>

        <UpgradeOptions reason={reason} />
      </div>
      </div>
    </div>
  );
}

/**
 * Renders one card per non-free tier. Highlights the smallest tier that
 * would unblock the action (`payload.requiredTier`). Used both inside the
 * modal AND on the standalone /upgrade landing page (which is the deep-link
 * destination for native).
 */
export function UpgradeOptions({ reason }: { reason: UpgradeReason | null }) {
  const catalog = useTierCatalog();
  const checkout = useStartCheckout();
  const [pendingTier, setPendingTier] = useState<PaidTier | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (catalog.isLoading) {
    return (
      <div className="py-6 text-center">
        <Spinner size="sm" />
      </div>
    );
  }
  if (catalog.isError || !catalog.data) {
    return (
      <p className="text-sm text-[color:var(--color-danger)]">
        Could not load pricing. Please try again in a moment.
      </p>
    );
  }

  const recommended =
    reason?.code === 'EARLY_PREDICTION' || reason?.code === 'VIEW_PREDICTIONS_EARLY'
      ? reason.requiredTier
      : reason?.payload.requiredTier ?? null;
  const currentTier =
    reason?.code === 'EARLY_PREDICTION' || reason?.code === 'VIEW_PREDICTIONS_EARLY'
      ? reason.currentTier
      : reason?.payload.currentTier ?? null;
  const offerable = catalog.data.filter((t) => t.tier !== PAYMENT_TIER_FREE);

  async function onUpgrade(tier: PaidTier) {
    setPendingTier(tier);
    setError(null);
    try {
      const res = await checkout.mutateAsync(tier);
      // Hand off to Stripe's hosted page via full-document navigation.
      window.location.assign(res.url);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not start checkout.');
      setPendingTier(null);
    }
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        {offerable.map((tier) => (
          <TierCard
            key={tier.tier}
            entry={tier}
            isRecommended={recommended === tier.tier}
            isCurrent={currentTier === tier.tier}
            isPending={pendingTier === tier.tier}
            disabled={pendingTier !== null}
            onPick={() => onUpgrade(tier.tier as PaidTier)}
          />
        ))}
      </div>
      {error ? (
        <p className="text-xs text-[color:var(--color-danger)]">{error}</p>
      ) : null}
      <p className="text-xs text-[color:var(--color-fg-muted)]">
        One-time payment processed securely by Stripe. No subscription.
      </p>
    </div>
  );
}

function TierCard({
  entry,
  isRecommended,
  isCurrent,
  isPending,
  disabled,
  onPick,
}: {
  entry: TierCatalogEntry;
  isRecommended: boolean;
  isCurrent: boolean;
  isPending: boolean;
  disabled: boolean;
  onPick: () => void;
}) {
  return (
    <div
      className="flex flex-col gap-3 rounded-xl border p-4"
      style={{
        borderColor: isRecommended ? 'var(--team-primary)' : 'var(--line)',
        background: isRecommended
          ? 'color-mix(in oklab, var(--team-primary) 8%, var(--bg))'
          : 'var(--bg)',
        boxShadow: isRecommended
          ? '0 0 0 1px color-mix(in oklab, var(--team-primary) 40%, transparent)'
          : undefined,
      }}
    >
      <div className="flex items-center justify-between gap-2">
        <h3
          className="font-semibold uppercase tracking-wide"
          style={{
            fontFamily: 'var(--team-font)',
            fontSize: '1rem',
            letterSpacing: '0.06em',
          }}
        >
          {entry.name}
        </h3>
        {isRecommended ? (
          <span
            className="mono"
            style={{
              fontSize: 10,
              background: 'var(--team-primary)',
              color: 'var(--team-on-primary)',
              padding: '3px 8px',
              borderRadius: 999,
              letterSpacing: '0.08em',
              fontWeight: 700,
            }}
          >
            RECOMMENDED
          </span>
        ) : null}
      </div>

      <div className="flex items-baseline gap-2">
        <span className="text-2xl font-semibold">
          {entry.price?.formatted ?? 'Free'}
        </span>
        {entry.price ? (
          <span className="text-xs text-[color:var(--color-fg-muted)]">
            one-time
          </span>
        ) : null}
      </div>

      <p className="text-xs text-[color:var(--color-fg-muted)]">
        {entry.description}
      </p>

      <ul className="flex flex-col gap-1 text-xs text-[color:var(--color-fg)]">
        {entry.features.map((f) => (
          <li key={f}>• {f}</li>
        ))}
      </ul>

      <button
        type="button"
        onClick={onPick}
        disabled={disabled || isCurrent}
        className="h-10 mt-auto cursor-pointer rounded-lg bg-[color:var(--team-primary)] px-4 text-sm font-medium text-[color:var(--team-on-primary)] disabled:pointer-events-none disabled:opacity-60"
      >
        {isCurrent
          ? 'Current plan'
          : isPending
            ? 'Redirecting…'
            : `Upgrade to ${entry.name}`}
      </button>
    </div>
  );
}

/**
 * Convenience wrapper for callers that just want to render the contents
 * inline (e.g. on the /upgrade landing page). Shares the same controls as
 * the modal.
 */
export function UpgradeOptionsInline({
  preselectedTier,
}: {
  preselectedTier: PaymentTier | null;
}) {
  const reason: UpgradeReason | null = preselectedTier
    ? {
        code: 'GROUP_QUOTA_EXCEEDED',
        payload: {
          currentTier: PAYMENT_TIER_FREE,
          requiredTier: preselectedTier === 'free' ? null : (preselectedTier as PaidTier),
          currentCount: 0,
          limit: 0,
        },
      }
    : null;
  return <UpgradeOptions reason={reason} />;
}
